import React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import format from 'helpers/translations';
import { formatReact } from 'modules/helpers/strings';
import InlineButton from '../InlineButton';
import type { ITable, ITableCell, ITableCellBase, Rows, TableHeaders } from './types';

const TableHeader = styled.thead<{ theme: any }>`
    ${({ theme: { headerColor } }) => headerColor && `background-color: ${headerColor};`}
`;

const TableRow = styled.tr<{ theme: any }>`
    ${({ theme: { alternateRowColors } }) =>
        alternateRowColors?.color &&
        alternateRowColors?.alternateOddRows &&
        css`
            tbody &:nth-child(${alternateRowColors.alternateOddRows ? 'odd' : 'even'}) {
                background-color: ${alternateRowColors.color};
            }
        `}

    ${({ theme: { divider } }) =>
        divider?.row &&
        divider?.color &&
        css`
            border-bottom: 1px solid ${divider.color};

            tbody &:last-child {
                border-bottom: 0;
            }
        `}
`;

export const TableCell = styled.td<{ bold?: boolean; theme: any }>`
    vertical-align: baseline;
    ${({ bold }) => bold && 'font-weight: bold;'}

    ${({ theme: { divider } }) =>
        divider?.column &&
        divider?.color &&
        css`
            & + & {
                border-left: 1px solid ${divider.color};
            }
        `}

    ${({ theme: { spacing } }) =>
        spacing &&
        css`
            ${typeof spacing === 'string' && `padding: ${spacing};`}
            ${
                spacing?.column &&
                css`
                padding-left: ${spacing.column};
                padding-right: ${spacing.column};

                &:first-child {
                    padding-left: 0;
                }

                &:last-child {
                    padding-right: 0;
                }
            `
            }
            ${
                spacing?.row &&
                css`
                padding-top: ${spacing.column};
                padding-bottom: ${spacing.column};
            `
            }
        `}
    
    ${({ theme: { align } }) =>
        align &&
        css`
            text-align: ${align?.base ? align.base : align};
            ${
                align.firstColumn &&
                css`
                &:first-child {
                    text-align ${align.firstColumn};
                }
            `
            }
            ${
                align.lastColumn &&
                css`
                &:last-child {
                    text-align ${align.lastColumn};
                }
            `
            }
        `}
`;

function getLongestArray(rows: Rows, tableHeaders?: TableHeaders) {
    return Math.max(tableHeaders?.length ?? 0, ...rows.map((row) => row.length));
}

function setArraysToSameLength(maxColumnAmount: number, rows: Rows, tableHeaders?: TableHeaders) {
    rows.forEach((row) => {
        row.push(...Array(maxColumnAmount - row.length));
    });

    tableHeaders?.push(...Array(maxColumnAmount - tableHeaders.length));
}

function TableCellContent({ value }: ITableCell) {
    if (typeof value === 'string') {
        return format(value);
    }

    return value;
}

const DefaultTableCell = ({ cell }) => {
    const { value = '', bold = false, colspan } = cell || {};

    return (
        <TableCell bold={bold} colSpan={colspan}>
            <TableCellContent value={value} />
        </TableCell>
    );
};

export default function Table<T extends {}>({
    rows,
    themeConfig = {},
    tableHeaders,
    cellRenderer = (cell) => <DefaultTableCell cell={cell} />,
    ...restProps
}: ITable<T>) {
    const hasViableHeader = !!tableHeaders?.length && !tableHeaders.some((item) => !item);
    const headersItems = hasViableHeader ? tableHeaders : undefined;
    const someCellsHasColSpan = rows.some((row) => row.some((cell) => cell?.colspan));

    if (!someCellsHasColSpan) {
        // if one of the cells has a colspan, then we will presume that you will control the layout.
        const maxColumnAmount = getLongestArray(rows, tableHeaders);
        setArraysToSameLength(maxColumnAmount, rows, tableHeaders);
    }

    return (
        <ThemeProvider theme={themeConfig as any}>
            <table {...restProps}>
                {headersItems && (
                    <TableHeader>
                        <TableRow>
                            {headersItems.map((tableHeader, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableCell as="th" key={i}>
                                    {tableHeader}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHeader>
                )}
                <tbody>
                    {rows.map((row, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <TableRow key={i}>
                            {row.map((cell, i) => (
                                <React.Fragment key={i}>{cellRenderer?.(cell)}</React.Fragment>
                            ))}
                        </TableRow>
                    ))}
                </tbody>
            </table>
        </ThemeProvider>
    );
}
